import React, { useState, useContext } from "react";
import {
    TextField,
    Button,
    Box,
    Dialog,
    DialogContent,
    Typography,
    InputAdornment,
    Alert,
    CircularProgress,
    IconButton,
    Stack,
    Fade,
    Link,
} from "@mui/material";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link as RouterLink } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

function LoginModal({ open, onClose }) {
    const [usernameInput, setUsernameInput] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const { login, loading } = useContext(AuthContext);

    const handleLogin = async () => {
        setError("");

        const result = await login(usernameInput, password);

        if (result.success) {
            setUsernameInput("");
            setPassword("");
            setShowPassword(false);
            onClose();
        } else {
            if (result.status === 401 || result.status === 400) {
                setError("Неверный логин или пароль. Проверьте введенные данные.");
            } else if (result.status === 403) {
                setError("Доступ запрещен. У вашей учетной записи недостаточно прав для входа.");
            } else {
                setError("Не удалось выполнить вход. Попробуйте еще раз позже.");
            }
        }
    };

    const handleClose = () => {
        if (loading) return;

        setError("");
        setUsernameInput("");
        setPassword("");
        setShowPassword(false);
        onClose();
    };

    const handleUsernameChange = (e) => {
        setUsernameInput(e.target.value);
        if (error) setError("");
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (error) setError("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (!loading && usernameInput.trim() && password.trim()) {
                handleLogin();
            }
        }
    };

    const submitDisabled = loading || !usernameInput.trim() || !password.trim();

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            TransitionComponent={Fade}
            transitionDuration={220}
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: "hidden",
                    border: "1px solid rgba(148, 163, 184, 0.18)",
                    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
                    background: "#ffffff",
                },
            }}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.25,
                    borderBottom: "1px solid rgba(148, 163, 184, 0.14)",
                    background: "linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "rgba(37, 99, 235, 0.10)",
                                    color: "#2563eb",
                                    border: "1px solid rgba(37, 99, 235, 0.12)",
                                }}
                            >
                                <ShieldOutlinedIcon fontSize="small" />
                            </Box>

                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: "1.1rem",
                                        fontWeight: 800,
                                        color: "#0f172a",
                                        lineHeight: 1.15,
                                        letterSpacing: "-0.02em",
                                    }}
                                >
                                    Вход в систему
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.35,
                                        fontSize: "0.9rem",
                                        color: "#64748b",
                                        lineHeight: 1.5,
                                    }}
                                >
                                    Используйте логин и пароль, выданный администратором сервиса
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <IconButton
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            mt: -0.25,
                            mr: -0.5,
                            width: 36,
                            height: 36,
                            color: "#64748b",
                            border: "1px solid rgba(148, 163, 184, 0.18)",
                            bgcolor: "#ffffff",
                            "&:hover": {
                                bgcolor: "rgba(37, 99, 235, 0.06)",
                                color: "#0f172a",
                            },
                        }}
                    >
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <DialogContent sx={{ px: 3, py: 3 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {error && (
                        <Alert
                            severity="error"
                            variant="outlined"
                            sx={{
                                borderRadius: 2.5,
                                alignItems: "center",
                                "& .MuiAlert-message": {
                                    fontSize: "0.9rem",
                                    lineHeight: 1.5,
                                },
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="Логин"
                        placeholder="Введите логин"
                        value={usernameInput}
                        onChange={handleUsernameChange}
                        onKeyDown={handleKeyDown}
                        error={!!error}
                        autoFocus
                        size="medium"
                        autoComplete="username"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2.5,
                                backgroundColor: "#ffffff",
                            },
                            "& .MuiFormHelperText-root": {
                                mx: 0.2,
                                mt: 0.8,
                                color: "#dc2626",
                                lineHeight: 1.45,
                            },
                        }}
                        helperText={
                            error && !usernameInput.trim()
                                ? "Введите логин"
                                : ""
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonOutlineRoundedIcon
                                        sx={{ color: "#94a3b8", fontSize: 20 }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Пароль"
                        placeholder="Введите пароль"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={handlePasswordChange}
                        onKeyDown={handleKeyDown}
                        error={!!error}
                        size="medium"
                        autoComplete="current-password"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2.5,
                                backgroundColor: "#ffffff",
                            },
                            "& .MuiFormHelperText-root": {
                                mx: 0.2,
                                mt: 0.8,
                                color: "#dc2626",
                                lineHeight: 1.45,
                            },
                        }}
                        helperText={
                            error && !password.trim()
                                ? "Введите пароль"
                                : ""
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon
                                        sx={{ color: "#94a3b8", fontSize: 20 }}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                                        sx={{ color: "#94a3b8" }}
                                    >
                                        {showPassword ? (
                                            <VisibilityOffOutlinedIcon fontSize="small" />
                                        ) : (
                                            <VisibilityOutlinedIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box
                        sx={{
                            mt: 0.25,
                            p: 1.5,
                            borderRadius: 2.5,
                            bgcolor: "rgba(248, 250, 252, 0.92)",
                            border: "1px solid rgba(148, 163, 184, 0.14)",
                        }}
                    >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                            <InfoOutlinedIcon
                                sx={{
                                    color: "#64748b",
                                    fontSize: 18,
                                    mt: "2px",
                                    flexShrink: 0,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: "0.82rem",
                                    color: "#64748b",
                                    lineHeight: 1.65,
                                }}
                            >
                                Продолжая вход, вы подтверждаете, что ознакомлены и соглашаетесь с{" "}
                                <Link
                                    component={RouterLink}
                                    to="/privacy"
                                    underline="hover"
                                    sx={{
                                        fontWeight: 700,
                                        color: "#2563eb",
                                    }}
                                >
                                    Политикой конфиденциальности
                                </Link>{" "}
                                и{" "}
                                <Link
                                    component={RouterLink}
                                    to="/terms"
                                    underline="hover"
                                    sx={{
                                        fontWeight: 700,
                                        color: "#2563eb",
                                    }}
                                >
                                    Пользовательским соглашением
                                </Link>
                                . Для восстановления доступа обратитесь через страницу{" "}
                                <Link
                                    component={RouterLink}
                                    to="/contacts"
                                    underline="hover"
                                    sx={{
                                        fontWeight: 700,
                                        color: "#2563eb",
                                    }}
                                >
                                    Контакты
                                </Link>
                                .
                            </Typography>
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            mt: 0.5,
                            display: "flex",
                            gap: 1.2,
                            justifyContent: "flex-end",
                            flexWrap: "wrap",
                        }}
                    >
                        <Button
                            onClick={handleClose}
                            disabled={loading}
                            variant="text"
                            sx={{
                                minWidth: 96,
                                borderRadius: 2.5,
                                textTransform: "none",
                                fontWeight: 700,
                                color: "#475569",
                                px: 2,
                            }}
                        >
                            Отмена
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleLogin}
                            disabled={submitDisabled}
                            startIcon={
                                loading ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <LoginRoundedIcon fontSize="small" />
                                )
                            }
                            sx={{
                                minWidth: 148,
                                borderRadius: 2.5,
                                textTransform: "none",
                                fontWeight: 700,
                                px: 2.2,
                                py: 1.15,
                                boxShadow: "none",
                            }}
                        >
                            {loading ? "Выполняется вход..." : "Войти"}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default LoginModal;